// @ts-ignore
import { useEffect } from 'react';
import { appActionTypes } from 'src/app/newActionTypes';
import { filtersInitialValues } from 'src/app/reducers/initialStates';
import { fetchFilterData } from 'src/app/resolvers/filtersResolver';
import { appAvailableFilters } from 'src/config';
import { DATA_FILTER_DIR } from 'src/constants';
import { useConfigSelectors, useFilterDataSelectors } from 'src/app/reducers';
import { DYNAMIC_FILTERS_GRAPHQL } from 'src/app/featureToggles';
import { useGetOperationsFilters } from 'src/app/gql/queries/filters/operationsFilters';
import { IOperationsFilters } from 'src/app/props';

// Operations filters loaded via GraphQL don't cover these - they either come
// from elsewhere (operationTags, see useOperationsFilter.ts) or don't yet
// have a GraphQL source (userEmails, changelogFields).
const OPERATIONS_FILTERS_NOT_IN_GRAPHQL: Pick<
  IOperationsFilters,
  'operationTags' | 'userEmails' | 'changelogFields'
> = {
  operationTags: [],
  userEmails: [],
  changelogFields: [],
};

export const loadFilters = (dispatcher, config, skipOperations = false): void => {
  const filters = (config?.globals?.data?.filters ?? {}) as Record<string, string | undefined>;

  const filterFileNameKeysAndSiteCodeValues = {
    infringements: filters.infringements ?? 'default',
    noiseEvents: filters.noiseEvents ?? 'default',
    ...(skipOperations ? {} : { operations: filters.operations ?? 'default' }),
    airtrak: filters.airtrak ?? 'default',
    complaints: filters.complaints ?? 'default',
    settings: filters.settings ?? 'default',
    modelingAircraftAssignments: filters.modelingAircraftAssignments ?? 'default',
    reports: filters.reports ?? 'default',
  };

  const appFilters = { ...filtersInitialValues };
  if (skipOperations) {
    // Operations filters are sourced from GraphQL (useLoadOperationsFilters) in this
    // mode - omit the key so the UPDATE_FILTERS dispatch below doesn't clobber that data.
    // @ts-ignore
    delete appFilters.operations;
  }

  const promises: any[] = [];
  let filtersToLoad = 'Filters to load: \n';
  Object.entries(filterFileNameKeysAndSiteCodeValues).forEach(([path, fileName]) => {
    if (
      fileName === 'default' ||
      appAvailableFilters.some(appFilter => {
        const filterString = `${DATA_FILTER_DIR}${path}/${fileName}`.toLowerCase();
        return (appFilter.toLowerCase() === filterString)
      }
      )
    ) {
      promises.push(fetchFilterData(path, fileName, appFilters));
      filtersToLoad += `\t ${path}: ${fileName}\n`;
    } else {
      promises.push(fetchFilterData(path, 'default', appFilters));
      filtersToLoad += `\t ${path}: using default ('${fileName}' specified, but not found)\n`;
    }
  });

  console.log(filtersToLoad);

  if (promises.length > 0) {
    Promise.all(promises)
      .then(() => {
        dispatcher({ type: appActionTypes.UPDATE_FILTERS, payload: appFilters });
      })
      .catch(() => {
        dispatcher({
          type: appActionTypes.FILTERS_ERROR,
          payload: appFilters,
        });
      });
  }
};

// Loads Operations page filter values via GraphQL instead of the static JSON
// file, gated behind the DYNAMIC_FILTERS_GRAPHQL site-config feature
// toggle. When disabled, this is a no-op and `loadFilters` continues to
// source operations filters from JSON as before.
export const useLoadOperationsFilters = (dispatcher): { loading: boolean } => {
  const configSelectors = useConfigSelectors();
  const filterDataSelectors = useFilterDataSelectors();
  const configLoaded = configSelectors.hasCompletedInitialLoad();
  const useGraphqlFilters = configLoaded
    ? configSelectors.isFeatureAvailable(DYNAMIC_FILTERS_GRAPHQL)
    : false;

  const { operationsFilters, error } = useGetOperationsFilters(
    !configLoaded || !useGraphqlFilters
  );

  useEffect(() => {
    if (!useGraphqlFilters || !operationsFilters) {
      return;
    }

    const mappedFilters: IOperationsFilters = {
      aircraftCategories: operationsFilters.aircraftCategories ?? [],
      operatorCategories: operationsFilters.operatorCategories ?? [],
      aircraftTypes: operationsFilters.aircraftTypes ?? [],
      airportIds: operationsFilters.airportIds ?? [],
      operationTypes: operationsFilters.operationTypes ?? [],
      remoteAirportIds: operationsFilters.remoteAirportIds ?? [],
      runwayNames: operationsFilters.runwayNames ?? [],
      airlines: operationsFilters.airlines ?? [],
      correlated: operationsFilters.correlated ?? [],
      pathname: operationsFilters.pathnames ?? [],
      ...OPERATIONS_FILTERS_NOT_IN_GRAPHQL,
    };

    dispatcher({ type: appActionTypes.UPDATE_OPERATIONS_FILTERS, payload: mappedFilters });
  }, [operationsFilters, useGraphqlFilters]);

  // Apollo's `data` resolves a render before the UPDATE_OPERATIONS_FILTERS
  // dispatch above (fired from the effect) is actually applied to the store.
  // Gating on `operationsFilters` directly would open the app-level loading
  // gate for that one render while state.filters.operations is still null,
  // crashing anything downstream that reads it (e.g. useOperationsFilter).
  // Gate on the store value itself so the two can never go out of sync.
  const operationsFilterDataLoaded = filterDataSelectors.getOperationsFilterData() != null;

  return {
    loading: useGraphqlFilters && !error && !operationsFilterDataLoaded,
  };
};
